import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FC, useEffect } from 'react';
import { Route } from 'react-router';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapNotesList } from './CareMapNotesList';
import { IonButtons, IonToolbar } from '@ionic/react';
import { AddNoteButton } from './add-note/AddNoteButton';
import { Text } from '../components/Text';
import { ICareMap } from '../caremap/ICareMap';
import { EditNoteWithQueryParamDrawer } from './edit-note/EditNoteWithQueryParamDrawer';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { AddNoteWithQueryParamDrawer } from './add-note/AddNoteWithQueryParamDrawer';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

export const NotesPageRouteHandler: React.FC = () => (
    <Route path="/notes/:caremapId" render={routeParams => (
        <CareMapNotesPage careMapId={routeParams.match.params.caremapId} />
    )} />
);

export const CareMapNotesPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <ActiveCaremapNotesPage />
        </ActiveCareMapProviderV2>
    );
};

export const ActiveCaremapNotesPage: React.FC = () => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);
    const addNoteDrawer = useInstance(AddNoteWithQueryParamDrawer);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    useEffect(() => {
        mixpanel.trackWithDebounce({ event: 'Notes Page Opened' });
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const activeCaremap = careMapState.getActiveCaremap();
    if (!activeCaremap){
        return null;
    }

    const caremap = careMapState.getActiveCaremap() as ICareMap;
    const notes = notesModelFactory.forActiveCaremap().getNotes();

    const patientFirstName = careMapState.getPatientFirstName(caremap);

    const title = `${patientFirstName}'s Notes`;

    const canEdit = activeCaremap.links.self.actions.includes('edit-notes');

    const editNote = (noteId: string | undefined) => {
        if (!noteId || noteId === 'add-note'){
            addNoteDrawer.open();
        }
        else {
            editNoteDrawer.open({ noteId });
        }
    };

    const addNote = () => {
        addNoteDrawer.open();
    };

    const canAddNotes = careMapState.hasPermissionTo('post-note');

    return (
        <InnerhivePage title={title}>
            <IonToolbar>
                <Text type="title">Notes</Text>
                {canAddNotes && (
                    <IonButtons slot="end">
                        <AddNoteButton
                            canAdd={canEdit}
                            onClick={addNote}
                        />
                    </IonButtons>
                )}
            </IonToolbar>

            {notesModelFactory.forActiveCaremap().hasLoaded && (
                <CareMapNotesList
                    notes={notes}
                    showHeading={false}
                    showNoteTargetHeader
                    editNoteHandler={editNote}
                />
            )}
        </InnerhivePage>
    );
};
